import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import PlaylistPlayRoundedIcon from "@mui/icons-material/PlaylistPlayRounded";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import AddBusinessRoundedIcon from "@mui/icons-material/AddBusinessRounded";

import { DRAFTS, FEEDBACK, REFERENCES, RUNS, TEAMS } from "./mockData";
import { Draft, Feedback, ReferenceWbr } from "./types";
import { AgentStatusChip } from "./components/StatusChip";
import Dashboard from "./pages/Dashboard";
import Runs from "./pages/Runs";
import Drafts from "./pages/Drafts";
import OnboardWbr from "./pages/OnboardWbr";

const DRAWER_WIDTH = 248;

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: <SpaceDashboardRoundedIcon /> },
  { key: "runs", label: "Runs", icon: <PlaylistPlayRoundedIcon /> },
  { key: "drafts", label: "Draft review", icon: <RateReviewRoundedIcon /> },
  { key: "onboard", label: "Onboard WBR", icon: <AddBusinessRoundedIcon /> },
];

const App: React.FC = () => {
  const [teamId, setTeamId] = useState(TEAMS[0].id);
  const [view, setView] = useState("dashboard");
  const [toast, setToast] = useState<string | null>(null);

  // Mutable copies so the UI is interactive against mock data.
  const [drafts, setDrafts] = useState<Draft[]>(DRAFTS);
  const [feedback, setFeedback] = useState<Feedback[]>(FEEDBACK);
  const [references, setReferences] = useState<ReferenceWbr[]>(REFERENCES);

  const team = useMemo(() => TEAMS.find((t) => t.id === teamId)!, [teamId]);

  const teamRuns = useMemo(
    () => RUNS.filter((r) => r.teamId === teamId),
    [teamId]
  );
  const teamDrafts = useMemo(
    () => drafts.filter((d) => d.teamId === teamId),
    [drafts, teamId]
  );
  const teamFeedback = useMemo(
    () => feedback.filter((f) => f.teamId === teamId),
    [feedback, teamId]
  );
  const teamRefs = useMemo(
    () => references.filter((r) => r.teamId === teamId),
    [references, teamId]
  );

  const triggerRun = () => setToast(`Manual run queued for ${team.name}.`);

  const submitFeedback = (
    draftId: string,
    section: string,
    comment: string
  ) => {
    const fb: Feedback = {
      id: `fb_${Date.now()}`,
      teamId,
      draftId,
      author: "you",
      createdAt: new Date().toISOString(),
      section,
      comment,
      source: "HUMAN",
    };
    setFeedback((prev) => [...prev, fb]);
    setDrafts((prev) =>
      prev.map((d) =>
        d.id === draftId ? { ...d, status: "CHANGES_REQUESTED" } : d
      )
    );
    setToast("Feedback submitted. It will inform the next run.");
  };

  const approveDraft = (draftId: string) => {
    setDrafts((prev) =>
      prev.map((d) => (d.id === draftId ? { ...d, status: "APPROVED" } : d))
    );
    setToast("Draft approved.");
  };

  const ingestReference = (title: string) => {
    setReferences((prev) => [
      {
        id: `ref_${Date.now()}`,
        teamId,
        title,
        ingestedAt: new Date().toISOString(),
        sizeKb: 150 + Math.round(Math.random() * 120),
        source: "manual upload",
      },
      ...prev,
    ]);
    setToast("Reference WBR ingested.");
  };

  const deleteReference = (id: string) => {
    setReferences((prev) => prev.filter((r) => r.id !== id));
    setToast("Reference removed.");
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            borderRight: "1px solid",
            borderColor: "divider",
            bgcolor: "#10142e",
            color: "#cdd3e6",
          },
        }}
      >
        <Box sx={{ px: 2.5, py: 2.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{
                background: "linear-gradient(135deg,#5a3fa0,#13b5b1)",
                width: 38,
                height: 38,
              }}
            >
              <AutoAwesomeRoundedIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ color: "#fff", lineHeight: 1.1 }}
              >
                AutoWBR
              </Typography>
              <Typography variant="caption" sx={{ color: "#8b93b3" }}>
                SPB Finance
              </Typography>
            </Box>
          </Stack>
        </Box>

        <List sx={{ px: 1.5 }}>
          {NAV.map((item) => {
            const active = view === item.key;
            return (
              <ListItemButton
                key={item.key}
                selected={active}
                onClick={() => setView(item.key)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  color: active ? "#fff" : "#aab1ce",
                  "& .MuiListItemIcon-root": {
                    color: active ? "#5fe8e3" : "#7d85a8",
                    minWidth: 38,
                  },
                  "&.Mui-selected": {
                    bgcolor: "rgba(95,232,227,0.12)",
                    "&:hover": { bgcolor: "rgba(95,232,227,0.18)" },
                  },
                  "&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                >
                  {item.label}
                </ListItemText>
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ mt: "auto", p: 2 }}>
          <Box
            sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(255,255,255,0.04)" }}
          >
            <Typography variant="caption" sx={{ color: "#8b93b3" }}>
              Active team agent
            </Typography>
            <Box sx={{ mt: 0.75 }}>
              <AgentStatusChip status={team.agentStatus} />
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Main area */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <AppBar
          position="sticky"
          color="inherit"
          sx={{
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Toolbar sx={{ gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Workspace
              </Typography>
              <Typography variant="subtitle1" sx={{ lineHeight: 1 }}>
                {NAV.find((n) => n.key === view)?.label}
              </Typography>
            </Box>

            <Chip
              size="small"
              variant="outlined"
              label={`${team.recipients} recipients`}
              sx={{ display: { xs: "none", sm: "flex" } }}
            />

            <FormControl size="small" sx={{ minWidth: 210 }}>
              <Select
                value={teamId}
                onChange={(e) => {
                  setTeamId(e.target.value);
                  setToast(null);
                }}
                renderValue={(val) => {
                  const t = TEAMS.find((x) => x.id === val)!;
                  return (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: 12,
                          bgcolor: "primary.main",
                        }}
                      >
                        {t.name.slice(0, 2).toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t.name}
                      </Typography>
                    </Stack>
                  );
                }}
              >
                {TEAMS.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    <Stack>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.description}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Avatar sx={{ bgcolor: "secondary.main", width: 36, height: 36 }}>
              Yo
            </Avatar>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            p: { xs: 2, md: 3.5 },
            flexGrow: 1,
            maxWidth: 1320,
            width: "100%",
            mx: "auto",
          }}
        >
          {view === "dashboard" && (
            <Dashboard
              team={team}
              runs={teamRuns}
              drafts={teamDrafts}
              onTriggerRun={triggerRun}
              onNavigate={setView}
            />
          )}
          {view === "runs" && (
            <Runs team={team} runs={teamRuns} onTriggerRun={triggerRun} />
          )}
          {view === "drafts" && (
            <Drafts
              team={team}
              drafts={teamDrafts}
              feedback={teamFeedback}
              references={teamRefs}
              onSubmitFeedback={submitFeedback}
              onApprove={approveDraft}
              onIngestReference={ingestReference}
              onDeleteReference={deleteReference}
            />
          )}
          {view === "onboard" && (
            <OnboardWbr
              onRegistered={(res) =>
                setToast(res.message || `WBR ${res.wbr_id} registered.`)
              }
            />
          )}
        </Box>
      </Box>

      <Snackbar
        open={toast != null}
        autoHideDuration={3200}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setToast(null)}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default App;
